import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import dayjs from "dayjs";
import { CarOutlined, CloseSquareOutlined, CopyOutlined, DollarOutlined, DownloadOutlined, FilePdfOutlined, MailOutlined, MinusCircleOutlined, PlusOutlined, QuestionCircleOutlined, ReloadOutlined, ShopOutlined } from '@ant-design/icons';
import { Button, Modal, Popover, Tag, Tooltip } from 'antd';
import axios, {} from "axios";
import HasPermissions from "../../../../../containers/HasPermissions";
import DateRangeDescription from "../../../components/DateRangeDescription";
import VehicleHireStatus from "../../../enums/VehicleHireStatus";
import PaymentApprovalStage from "../../../enums/PaymentApprovalStage";
import { axiosErrorHandler, isConcurrentModificationError, isPaymentRequiredError, showErrorNotification, showSuccessNotification } from "../../../../../swzutils";
import { getDivisionName, userHasPermission, isUserMarketplacePayAsYouGo, isUserMultiTenant, shouldShowDivision } from "../../../../../swzutils/UserPermissionsUtil";
import DeallocateVehicleModal from "../DeallocateVehicleModal";
import VehicleHireAllocateVehicleModal, { VehicleHireAllocateTab } from "../VehicleHireAllocateVehicleModal";
import VehicleHirePaymentMethod from "../../../enums/VehicleHirePaymentMethod";
import VehicleHireStatusText from "../VehicleHireStatusText";
import SpinnerModal from "../../../components/SpinnerModal";
import HelpLabel from "../../../components/HelpLabel";
import GlobalConstants from "../../../../../swzutils/GlobalConstants";
import VehicleHireApprovePaymentModal from "./VehicleHireApprovePaymentModal";
import VehicleHireEmailDocketModal from "../VehicleHireEmailDocketModal";
import UnitOfMeasureType from "../../../enums/UnitOfMeasureType";
import MarketplaceFeesList from "../MarketplaceFeesList";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import { VehicleHirePaymentMethodEnum, VehicleHireStatusEnum } from "../api/Representation";
import { useNavigate, useSearchParams } from "react-router-dom";
import PaginatedFilterableTable, {} from "../../../components/table/PaginatedFilterableTable";
import API from "../../../../../API";
import StripePurchaseModal from "../../billing/StripePurchaseModal";
import VehicleHireResource from "../api/VehicleHireResource";
import { CurrencyFormat } from "../../../components/FormattersTS";
import PermissionButton from "../../../components/PermissionButton";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../../store/slice/auth";
import { useAppSelector } from "../../../../../store";
export const POST_VEHICLE_HIRE_ON_MARKETPLACE_ID_QUERY_PARAM_NAME = "postOnMarketplaceVehicleHireId";
const VehicleHiresTable = () => {
    const [deallocateVehicleHire, setDeallocateVehicleHire] = useState();
    const [allocateVehicleHire, setAllocateVehicleHire] = useState();
    const [paymentRequiredVehicleHire, setPaymentRequiredVehicleHire] = useState();
    const [actionInProgressMessage, setActionInProgressMessage] = useState(undefined);
    const [emailDocketVehicleHire, setEmailDocketVehicleHire] = useState();
    const [paymentApprovalVehicleHire, setPaymentApprovalVehicleHire] = useState();
    const tableRef = useRef(null);
    const [modal, contextHolder] = Modal.useModal();
    const navigate = useNavigate();
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const [searchParams] = useSearchParams();
    useEffect(() => {
        if (searchParams.has(POST_VEHICLE_HIRE_ON_MARKETPLACE_ID_QUERY_PARAM_NAME)) {
            setActionInProgressMessage("Retrieving vehicle request");
            VehicleHireResource.getVehicleHire(Number(searchParams.get(POST_VEHICLE_HIRE_ON_MARKETPLACE_ID_QUERY_PARAM_NAME)), setPaymentApprovalVehicleHire, axiosErrorHandler, () => setActionInProgressMessage(undefined));
        }
    }, []);
    const refresh = () => {
        tableRef.current?.fetch();
    };
    const gotoEdit = (vehicleHire) => {
        navigate(`/app/vehicle-hires/edit/${vehicleHire.id}`);
    };
    const confirmCancel = (vehicleHire) => {
        modal.confirm({
            title: 'Are you sure?',
            content: 'This action will cancel this vehicle request.  Requests cannot be uncancelled.',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => {
                performCancel(vehicleHire);
            }
        });
    };
    const performCancel = (vehicleHire) => {
        setActionInProgressMessage("Cancelling " + vehicleHire.ref);
        axios.delete(`/admin/v1/vehicle-hires/${vehicleHire.id}`)
            .then(( /*response*/) => tableRef.current?.removeRecordById(vehicleHire.id))
            .catch(axiosErrorHandler)
            .finally(() => setActionInProgressMessage(undefined));
    };
    const showDeallocateReasonDialog = (vehicleHire) => {
        setDeallocateVehicleHire(vehicleHire);
    };
    const onDeallocated = (vehicleHire) => {
        tableRef.current?.updateRecord(vehicleHire);
        setDeallocateVehicleHire(undefined);
    };
    const showAllocateVehicleDialog = (vehicleHire) => {
        setAllocateVehicleHire(vehicleHire);
    };
    const onAllocated = (vehicleHire) => {
        tableRef.current?.updateRecord(vehicleHire);
        setAllocateVehicleHire(undefined);
    };
    const downloadCsv = (fullFinancials) => {
        const suffix = fullFinancials ? "-FullFinancials" : "";
        const additionalQueryParams = {
            "fullFinancials": fullFinancials
        };
        tableRef.current?.downloadCurrentFilterAsCsv("vehicleRequests" + suffix, "/csv", additionalQueryParams);
    };
    const hideEmailDocketModal = () => {
        setEmailDocketVehicleHire(undefined);
    };
    const showPaymentApprovalStageModal = (vehicleHire) => {
        setPaymentApprovalVehicleHire(vehicleHire);
    };
    const hidePaymentApprovalStageModal = () => {
        setPaymentApprovalVehicleHire(undefined);
    };
    const postOnMarketplace = (vehicleHire, stripeSourceId) => {
        setActionInProgressMessage("Posting on Marketplace");
        setPaymentRequiredVehicleHire(undefined);
        let url = `/admin/v1/vehicle-hires/${vehicleHire.id}/marketplace`;
        if (stripeSourceId) {
            url += `?stripeSourceId=${stripeSourceId}`;
        }
        axios.post(url)
            .then(response => tableRef.current?.updateRecord(response.data))
            .catch(error => {
            if (isConcurrentModificationError(error)) {
                showErrorNotification("The vehicle request could not be placed on marketplace. This is because another user has updated the same vehicle request.  Please refresh the page and try again.");
            }
            else if (isPaymentRequiredError(error)) {
                setPaymentRequiredVehicleHire(vehicleHire);
            }
            else {
                axiosErrorHandler(error);
            }
        })
            .finally(() => setActionInProgressMessage(undefined));
    };
    const withholdFromMarketplace = (vehicleHire) => {
        setActionInProgressMessage("Withdrawing from Marketplace");
        axios.delete(`/admin/v1/vehicle-hires/${vehicleHire.id}/marketplace?version=${vehicleHire.version}`)
            .then(response => tableRef.current?.updateRecord(response.data))
            .catch(axiosErrorHandler)
            .finally(() => setActionInProgressMessage(undefined));
    };
    const onCreateMarketplacePaymentIntent = () => {
        return axios.post(`/admin/v1/vehicle-hires/${paymentRequiredVehicleHire?.id}/marketplace/payment-intent`);
    };
    const hasPaymentApprovalPermission = () => {
        return userHasPermission(loggedInUser, ['admin:vehicleHires:paymentApproval']) && !isUserMarketplacePayAsYouGo(loggedInUser, selectedTenant);
    };
    const hasMutateAfterInvoicedPermission = () => {
        return userHasPermission(loggedInUser, ['subtruxAdminAccounts:mutateVehicleHireAfterInvoiced']);
    };
    const divisionName = getDivisionName(loggedInUser, selectedTenant);
    const columns = [{
            title: 'Date',
            key: 'hireDate',
            dataIndex: 'hireDate',
            width: "9%",
            filter: {
                type: 'dateRange',
                requestKey: 'hireFromDate',
                requestKeyDateTo: 'hireToDate'
            },
            render: (text, record) => (_jsx(DateRangeDescription, { fromISOTimestamp: record.hireFrom, toISOTimestamp: record.hireTo }))
        }, {
            title: 'Ref',
            key: 'ref',
            dataIndex: 'ref',
            width: '7%',
            filter: {
                type: 'text',
                requestKey: 'refLike',
                placeholder: 'Search ref'
            },
            sorter: true,
            sortFilter: {
                requestKey: 'orderBy',
                ascending: 'REF_ASC',
                descending: 'REF_DESC'
            }
        }, {
            title: 'Pay by',
            key: 'paymentMethod',
            dataIndex: 'paymentMethod',
            width: "2%",
            filter: {
                type: 'enum',
                requestKey: 'paymentMethod',
                enums: VehicleHirePaymentMethod.filtersArray(),
                filterMultiple: true
            },
            render: (text /*, record*/) => {
                const enumValue = VehicleHirePaymentMethod.valueOf(text);
                if (enumValue) {
                    return enumValue.renderTag();
                }
                return text;
            }
        }, {
            title: 'Start Location',
            key: 'startLocation',
            width: "9%",
            render: (text, record) => {
                if (record.locations && record.locations.length) {
                    const startingLocation = record.locations.find((location) => location.type === 'STARTING');
                    if (startingLocation) {
                        const address = startingLocation.address;
                        return (_jsxs(_Fragment, { children: [startingLocation.companyName, _jsx("br", {}), address.suburb] }));
                    }
                }
                return null;
            }
        }, {
            title: 'Delivery Location',
            key: 'deliveryLocation',
            width: "9%",
            filter: {
                type: 'text',
                requestKey: 'deliveryCompanyNameLike',
                placeholder: 'Company Name'
            },
            render: (text, record) => {
                if (record.locations && record.locations.length) {
                    const firstDeliveryLocation = record.locations.find((location) => location.type === 'DELIVERY');
                    if (firstDeliveryLocation) {
                        const address = firstDeliveryLocation.address;
                        return (_jsxs(_Fragment, { children: [firstDeliveryLocation.companyName, _jsx("br", {}), address.suburb] }));
                    }
                }
                return null;
            }
        }, {
            title: 'Vehicle Type',
            key: "preferredVehicleType",
            dataIndex: ['category', 'description'],
            filter: {
                type: 'text',
                requestKey: 'preferredVehicleTypeLike',
                placeholder: 'Category'
            },
            render: (text, record) => {
                const { features, preferredVehicleType } = record;
                let featureTags = [];
                if (features && features.length > 0) {
                    featureTags = features.map(feature => {
                        return _jsx(Tag, { children: feature.description }, feature.id);
                    });
                }
                return _jsxs(_Fragment, { children: [preferredVehicleType?.description, _jsx("br", {}), featureTags] });
            },
            width: "10%"
        }, {
            title: _jsxs(Tooltip, { title: "Excluding GST", children: ["Rate ", _jsx(QuestionCircleOutlined, {})] }),
            key: 'rate',
            width: "5%",
            className: "nowrap",
            render: (text, record) => {
                if (record.paymentMethod === VehicleHirePaymentMethodEnum.HOUR) {
                    const hourlyRate = record.nominatedHourlyRate ? record.nominatedHourlyRate : record.hourlyRate;
                    return (_jsxs(_Fragment, { children: [_jsx(CurrencyFormat, { value: hourlyRate }), " / hr"] }));
                }
                if (record.paymentMethod === VehicleHirePaymentMethodEnum.LOAD && record.costPerUnitOfMeasure && record.loadRateUnitOfMeasure) {
                    const loadRateUnitOfMeasure = UnitOfMeasureType.valueOf(record.loadRateUnitOfMeasure);
                    return (_jsxs(_Fragment, { children: [_jsx(CurrencyFormat, { value: record.costPerUnitOfMeasureIncRebate }), " / ", loadRateUnitOfMeasure?.shortDescription, record.rebateAmountPerUomExTax && _jsxs(_Fragment, { children: [_jsx("br", {}), _jsxs("small", { children: ["(inc ", _jsx(CurrencyFormat, { value: record.rebateAmountPerUomExTax }), " rebate)"] })] })] }));
                }
                return _jsx(_Fragment, { children: "\u2014" });
            }
        }, {
            title: _jsxs(Tooltip, { title: "Including Fuel Levy but Excluding GST", children: ["Cost ", _jsx(QuestionCircleOutlined, {})] }),
            key: 'actualServiceCostToTenant',
            dataIndex: 'actualServiceCostToTenant',
            width: "5%",
            className: "nowrap",
            render: (text, vehicleHire) => (_jsx(CurrencyFormat, { value: vehicleHire.actualServiceCostToTenant || vehicleHire.estServiceCostToTenant }))
        }, {
            title: _jsxs(Tooltip, { title: "Excluding GST", children: ["Fees ", _jsx(QuestionCircleOutlined, {})] }),
            key: 'fees',
            dataIndex: 'fees',
            width: "5%",
            className: "nowrap",
            render: (text, vehicleHire) => (_jsx(MarketplaceFeesList, { fees: vehicleHire.fees }))
        }, {
            title: 'Profile',
            dataIndex: ['tenant', 'name'],
            sorter: false,
            width: '6%',
            condition: isUserMultiTenant(loggedInUser)
        }, {
            title: divisionName,
            dataIndex: 'division',
            sorter: false,
            width: '5%',
            filter: {
                type: 'text',
                requestKey: 'divisionNameLike',
                placeholder: divisionName
            },
            condition: shouldShowDivision(loggedInUser, selectedTenant),
            render: (text, record) => {
                if (record.division) {
                    return _jsx(Tag, { children: record.division.name }, record.division.id);
                }
                return null;
            }
        }, {
            title: 'Rego',
            dataIndex: 'vehicleRegistration',
            width: "5%",
            className: "nowrap",
            filter: {
                type: 'text',
                requestKey: 'registrationLike',
                placeholder: 'Registration',
                forceUppercase: true
            },
            render: (text, record) => {
                return record.allocatedVehicle ? _jsx("span", { children: record.allocatedVehicle.registration }) : null;
            },
        }, {
            title: 'Partner',
            dataIndex: 'tenant',
            width: "9%",
            filter: {
                type: 'text',
                requestKey: 'allocatedVehicleTenantNameLike',
                placeholder: 'Partner Name'
            },
            render: (text, record) => {
                if (record.allocatedVehicle) {
                    return record.allocatedVehicle.tenantName;
                }
                return null;
            }
        }, {
            title: 'Allocated Type',
            dataIndex: ['allocatedVehicle', 'type', 'description'],
            sorter: false,
            width: '10%'
        }, {
            title: 'Status',
            dataIndex: 'status',
            width: "10%",
            filter: {
                type: 'enum',
                requestKey: 'status',
                enums: VehicleHireStatus.filtersArray(),
                filterMultiple: true
            },
            render: (text, vehicleHire) => (_jsx(VehicleHireStatusText, { vehicleHire: vehicleHire }))
        }, {
            title: 'Payment Approved',
            dataIndex: 'paymentApprovalStage',
            width: "5%",
            filter: {
                type: 'enum',
                requestKey: 'paymentApprovalStage',
                enums: PaymentApprovalStage.filtersArray(),
                filterMultiple: true
            },
            condition: hasPaymentApprovalPermission(),
            render: (text, vehicleHire) => {
                if (vehicleHire.status === VehicleHireStatusEnum.CANCELLED) {
                    return "";
                }
                const paymentApprovalStage = PaymentApprovalStage.valueOf(text);
                if (!paymentApprovalStage) {
                    return "";
                }
                const anchor = paymentApprovalStage !== PaymentApprovalStage.PAID || hasMutateAfterInvoicedPermission()
                    ? (_jsx("a", { onClick: () => showPaymentApprovalStageModal(vehicleHire), children: paymentApprovalStage.renderTag() }))
                    : paymentApprovalStage.renderTag();
                if (vehicleHire.paymentApprovalByUser && vehicleHire.paymentApprovalChangedAt) {
                    return (_jsx(Popover, { content: _jsxs("p", { children: ["By: ", _jsxs("strong", { children: [vehicleHire.paymentApprovalByUser.firstName, "\u00A0", vehicleHire.paymentApprovalByUser.lastName] }), _jsx("br", {}), "at: ", dayjs(vehicleHire.paymentApprovalChangedAt).format("DD/MM/YYYY HH:mm")] }), children: anchor }));
                }
                return anchor;
            }
        }, {
            title: 'Action',
            width: "10%",
            render: (text, record) => {
                if (record.status === "COMPLETED") {
                    const paymentApprovalStage = PaymentApprovalStage.valueOf(record.paymentApprovalStage);
                    return (_jsx(PermissionDropdownButton, { label: "View", onClick: () => gotoEdit(record), permissions: ['admin:vehicleHires:view'], menuItems: [{
                                key: "app/vehicle-hires/docket/download",
                                permissions: ['admin:vehicleHireDocument:docket'],
                                route: `${API.origin()}/admin/v1/vehicle-hires/${record.id}/documents/docket`,
                                target: GlobalConstants.PDF_WINDOW_TARGET_NAME,
                                icon: _jsx(FilePdfOutlined, {}),
                                label: "Docket PDF"
                            }, {
                                key: "app/vehicle-hires/docket/email",
                                permissions: ['admin:vehicleHireDocument:docket'],
                                icon: _jsx(MailOutlined, {}),
                                label: "Email Docket",
                                onClick: () => setEmailDocketVehicleHire(record)
                            }, {
                                key: 'app/vehicle-hire/clone',
                                permissions: ['admin:vehicleHires:create:payBy:' + record.paymentMethod],
                                route: `#/app/vehicle-hires/clone/${record.id}`,
                                icon: _jsx(CopyOutlined, {}),
                                label: "Duplicate/Clone",
                            }, {
                                key: 'app/vehicle-hire/approve-payment',
                                permissions: ['admin:vehicleHires:paymentApproval'],
                                onClick: () => showPaymentApprovalStageModal(record),
                                condition: hasPaymentApprovalPermission() && paymentApprovalStage !== PaymentApprovalStage.PAID || hasMutateAfterInvoicedPermission(),
                                icon: _jsx(DollarOutlined, {}),
                                label: paymentApprovalStage === PaymentApprovalStage.PENDING ? 'Approve Payment' : 'Revise Payment'
                            }] }));
                }
                if (record.status === "CANCELLED") {
                    return (_jsx(Button, { htmlType: "button", onClick: () => gotoEdit(record), children: "View" }));
                }
                const canEdit = userHasPermission(loggedInUser, ['admin:vehicleHires:update:payBy:' + record.paymentMethod]);
                return (_jsx("span", { className: "actions", children: _jsx(PermissionDropdownButton, { label: canEdit ? "Edit" : "View", onClick: () => gotoEdit(record), permissions: ['admin:vehicleHires:view'], menuItems: [{
                                key: "app/vehicle-hires/postOnMarketplace",
                                icon: _jsx(ShopOutlined, {}),
                                label: "Post on marketplace",
                                condition: canEdit && record.status === "WITHHELD",
                                onClick: () => postOnMarketplace(record, null)
                            }, {
                                key: "app/vehicle-hires/withholdFromMarketplace",
                                label: "Withhold from Marketplace",
                                icon: _jsx(MinusCircleOutlined, {}),
                                condition: canEdit && record.status === "ON_MARKET",
                                onClick: () => withholdFromMarketplace(record)
                            }, {
                                key: "app/vehicle-hires/cancel",
                                permissions: ['admin:vehicleHires:delete'],
                                label: "Cancel",
                                icon: _jsx(CloseSquareOutlined, {}),
                                onClick: () => confirmCancel(record)
                            }, {
                                key: "app/vehicle-hires/deallocate",
                                permissions: ['admin:vehicleHires:deallocate'],
                                label: "Deallocate",
                                icon: _jsx(MinusCircleOutlined, {}),
                                condition: record.allocatedVehicle && record.status !== "STARTED",
                                onClick: () => showDeallocateReasonDialog(record)
                            }, {
                                key: "app/vehicle-hires/allocate",
                                permissions: ['admin:vehicleHires:allocateToPartner'],
                                label: "Allocate",
                                icon: _jsx(CarOutlined, {}),
                                condition: !record.allocatedVehicle && (record.status === "WITHHELD" || record.status === "ON_MARKET"),
                                onClick: () => showAllocateVehicleDialog(record)
                            }, {
                                key: 'app/vehicle-hire/clone',
                                permissions: ['admin:vehicleHires:create:withoutSalesOrder'],
                                route: `#/app/vehicle-hires/clone/${record.id}`,
                                icon: _jsx(CopyOutlined, {}),
                                label: "Duplicate/Clone"
                            }] }) }));
            }
        }];
    return (_jsxs("article", { className: "article swz-fixed-table-article", children: [contextHolder, _jsx("h2", { className: "article-title", children: _jsx(HelpLabel, { title: "Vehicle & Driver Hires", size: "large", iconSize: "large", helpText: _jsxs("div", { children: [_jsx("p", { children: "Daily hires allow you to request a vehicle at your request for an extended period of time" }), _jsxs("ul", { children: [_jsx("li", { children: "Pay by the load" }), _jsx("li", { children: "Pay by the hour" }), _jsx("li", { children: "Pay by the delivery" })] }), _jsx("p", { children: "Select request a daily hire for more in depth information" })] }) }) }), _jsxs("div", { className: "table-operations clearfix", children: [_jsx(HasPermissions, { permissions: ['admin:vehicleHires:create:withoutSalesOrder'], children: _jsx(Button, { href: "#/app/vehicle-hires/create", title: "Vehicle & Driver Hires", icon: _jsx(PlusOutlined, {}), children: "Vehicle & Driver Hires" }) }), _jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) }), _jsx(PermissionButton, { permissions: ['admin:vehicleHires:exportCsv'], className: "pull-right", onClick: () => downloadCsv(false), title: "Download CSV", icon: _jsx(DownloadOutlined, {}), children: "CSV" }), userHasPermission(loggedInUser, ['subtruxOperations:viewContractorRates']) && userHasPermission(loggedInUser, ['subtruxOperations:viewAdminFees']) &&
                        _jsx(PermissionButton, { permissions: ['admin:vehicleHires:exportCsv'], className: "pull-right", onClick: () => downloadCsv(true), title: "Download CSV", icon: _jsx(DollarOutlined, {}), children: "Full Financials CSV" })] }), _jsx(PaginatedFilterableTable, { tableKey: 'vehicleHireTable', ref: tableRef, dataUri: "/admin/v1/vehicle-hires", columns: columns, refreshOnSelectedTenantChanged: true, initialFilters: {
                    "status": VehicleHireStatus.defaultFilters()
                } }), _jsx(VehicleHireApprovePaymentModal, { open: !!paymentApprovalVehicleHire, vehicleHire: paymentApprovalVehicleHire, onCancel: hidePaymentApprovalStageModal, onApproved: vehicleHire => {
                    hidePaymentApprovalStageModal();
                    tableRef.current?.updateRecord(vehicleHire);
                } }), _jsx(DeallocateVehicleModal, { open: !!(deallocateVehicleHire && deallocateVehicleHire.id), vehicleHireId: deallocateVehicleHire && deallocateVehicleHire.id, vehicleHireVersion: deallocateVehicleHire && deallocateVehicleHire.version, allocatedVehicleId: deallocateVehicleHire && deallocateVehicleHire.allocatedVehicle && deallocateVehicleHire.allocatedVehicle.id, onCancel: () => setDeallocateVehicleHire(undefined), onDeallocated: onDeallocated }), _jsx(VehicleHireAllocateVehicleModal, { visibleTabs: [VehicleHireAllocateTab.PARTNER], open: !!(allocateVehicleHire && allocateVehicleHire.id), vehicleHireTenantId: allocateVehicleHire && allocateVehicleHire.tenant.id, vehicleHireId: allocateVehicleHire && allocateVehicleHire.id, vehicleHireVersion: allocateVehicleHire && allocateVehicleHire.version, onCancel: () => {
                    setAllocateVehicleHire(undefined);
                    refresh();
                }, onAllocated: onAllocated }), _jsx(StripePurchaseModal, { open: !!paymentRequiredVehicleHire, title: "Credit card required", onCreatePaymentIntent: onCreateMarketplacePaymentIntent, returnUrl: `/#/app/billing/tenant?${POST_VEHICLE_HIRE_ON_MARKETPLACE_ID_QUERY_PARAM_NAME}=${paymentRequiredVehicleHire?.id}`, onCancel: () => setPaymentRequiredVehicleHire(undefined), onPurchaseComplete: () => {
                    setPaymentRequiredVehicleHire(undefined);
                    showSuccessNotification(`Vehicle request ${paymentRequiredVehicleHire?.ref} successfully posted on marketplace`);
                    refresh();
                } }), _jsx(VehicleHireEmailDocketModal, { open: !!(emailDocketVehicleHire && emailDocketVehicleHire.id), vehicleHireId: emailDocketVehicleHire && emailDocketVehicleHire.id, onCancel: hideEmailDocketModal, onComplete: hideEmailDocketModal }), _jsx(SpinnerModal, { open: !!actionInProgressMessage, message: actionInProgressMessage })] }));
};
export default VehicleHiresTable;
